import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PERMISSION_REGISTRY } from '../../../config/permissionsRegistry';
import usePermission from '../../../hooks/usePermission';

const PermissionMatrix = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchModule, setSearchModule] = useState('');
  
  // Selected permissions state: Set of "module.action"
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [initialPermissions, setInitialPermissions] = useState(new Set());
  const [isDirty, setIsDirty] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` }
  });

  const fetchRoleData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/superadmin/roles/${id}`, getAuthHeader());
      const roleData = res.data.role;
      setRole(roleData);

      const permSet = new Set();
      if (roleData.Permissions) {
        roleData.Permissions.forEach(p => {
          permSet.add(`${p.module}.${p.action}`);
        });
      }

      setSelectedPermissions(permSet);
      setInitialPermissions(new Set(permSet));
      setIsDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load role permissions');
      navigate('/superadmin/master-roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleData();
  }, [id]);

  // Helper check
  const isChecked = (module, actionKey) => {
    return selectedPermissions.has(`${module}.${actionKey}`);
  };

  // Toggle individual action checkbox with dependency rules
  const handleCheckboxChange = (module, actionKey) => {
    const updated = new Set(selectedPermissions);
    const key = `${module}.${actionKey}`;

    if (actionKey === 'view') {
      if (updated.has(key)) {
        // Unchecking View -> Remove View and ALL dependent actions for this module
        const modDef = PERMISSION_REGISTRY.find(m => m.module === module);
        if (modDef) {
          modDef.actions.forEach(a => {
            updated.delete(`${module}.${a.key}`);
          });
        }
      } else {
        // Checking View
        updated.add(key);
      }
    } else {
      // Non-view action (Add, Edit, Delete, Export, etc.)
      if (updated.has(key)) {
        updated.delete(key);
      } else {
        // Checking an action -> Automatically check View as well!
        updated.add(key);
        updated.add(`${module}.view`);
      }
    }

    setSelectedPermissions(updated);
    setIsDirty(true);
  };

  // Select All for a specific module
  const handleSelectAllModule = (module) => {
    const updated = new Set(selectedPermissions);
    const modDef = PERMISSION_REGISTRY.find(m => m.module === module);
    if (modDef) {
      modDef.actions.forEach(a => {
        updated.add(`${module}.${a.key}`);
      });
    }
    setSelectedPermissions(updated);
    setIsDirty(true);
  };

  // Clear All for a specific module
  const handleClearAllModule = (module) => {
    const updated = new Set(selectedPermissions);
    const modDef = PERMISSION_REGISTRY.find(m => m.module === module);
    if (modDef) {
      modDef.actions.forEach(a => {
        updated.delete(`${module}.${a.key}`);
      });
    }
    setSelectedPermissions(updated);
    setIsDirty(true);
  };

  // Global Select All across all modules
  const handleGlobalSelectAll = () => {
    const updated = new Set();
    PERMISSION_REGISTRY.forEach(mod => {
      mod.actions.forEach(a => {
        updated.add(`${mod.module}.${a.key}`);
      });
    });
    setSelectedPermissions(updated);
    setIsDirty(true);
  };

  // Global Clear All
  const handleGlobalClearAll = () => {
    setSelectedPermissions(new Set());
    setIsDirty(true);
  };

  // Save permissions to backend API
  const handleSave = async () => {
    setSaving(true);
    try {
      const payloadPermissions = [];
      selectedPermissions.forEach(permStr => {
        const [mod, act] = permStr.split('.');
        if (mod && act) {
          payloadPermissions.push({ module: mod, action: act });
        }
      });

      const res = await axios.put(
        `${API_BASE}/superadmin/roles/${id}/permissions`,
        { permissions: payloadPermissions },
        getAuthHeader()
      );

      toast.success(res.data.message || 'Permissions saved successfully');
      setInitialPermissions(new Set(selectedPermissions));
      setIsDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty && !window.confirm('You have unsaved permission changes. Are you sure you want to discard them?')) {
      return;
    }
    navigate('/superadmin/master-roles');
  };

  const filteredRegistry = PERMISSION_REGISTRY.filter(item =>
    item.label.toLowerCase().includes(searchModule.toLowerCase()) ||
    item.module.toLowerCase().includes(searchModule.toLowerCase())
  );

  return (
    <div className="page-content pb-5">
      <div className="container-fluid">

        {/* Page Title */}
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">Role Permission Matrix</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><Link to="/superadmin/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/superadmin/master-roles">Master Roles</Link></li>
                  <li className="breadcrumb-item active">Permission Matrix</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-2 text-muted">Loading permission matrix...</p>
          </div>
        ) : (
          <>
            {/* Role Summary Banner */}
            <div className="card shadow-sm border-0 mb-4 bg-primary text-white">
              <div className="card-body p-4">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                  <div>
                    <h3 className="text-white fw-bold mb-1">
                      <i className="ri-shield-user-line me-2"></i> Role: {role?.name}
                    </h3>
                    <p className="mb-0 text-white-50">
                      {role?.description || 'Configure granular module & action access control for employees.'}
                    </p>
                  </div>
                  <div className="mt-3 mt-md-0 d-flex align-items-center gap-2">
                    <span className="badge bg-white text-primary fs-13 px-3 py-2">
                      <i className="ri-key-2-line me-1"></i> {selectedPermissions.size} Active Permission(s)
                    </span>
                    {isDirty && (
                      <span className="badge bg-warning text-dark fs-12 px-2 py-2 animate-pulse">
                        <i className="ri-error-warning-line me-1"></i> Unsaved Changes
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Matrix Control Bar */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <div className="row align-items-center g-3">
                  <div className="col-md-5">
                    <div className="position-relative">
                      <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" style={{ pointerEvents: 'none', zIndex: 5 }}></i>
                      <input
                        type="text"
                        className="form-control ps-5"
                        style={{ paddingLeft: '45px' }}
                        placeholder="Search modules..."
                        value={searchModule}
                        onChange={(e) => setSearchModule(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-7 text-md-end d-flex gap-2 justify-content-md-end flex-wrap">
                    <button className="btn btn-soft-success btn-sm" onClick={handleGlobalSelectAll}>
                      <i className="ri-checkbox-multiple-line me-1"></i> Select All Modules
                    </button>
                    <button className="btn btn-soft-danger btn-sm" onClick={handleGlobalClearAll}>
                      <i className="ri-close-circle-line me-1"></i> Clear All Modules
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modules Matrix Cards */}
            <div className="row">
              {filteredRegistry.length === 0 ? (
                <div className="col-12 text-center py-4 text-muted">
                  <p>No modules match your search filter.</p>
                </div>
              ) : (
                filteredRegistry.map((mod) => {
                  const viewChecked = isChecked(mod.module, 'view');
                  const allModuleActionsChecked = mod.actions.every(a => isChecked(mod.module, a.key));

                  return (
                    <div className="col-xl-6 col-lg-12 mb-4" key={mod.module}>
                      <div className={`card h-100 shadow-sm border ${viewChecked ? 'border-primary-subtle' : 'border-light'}`}>
                        <div className="card-header bg-light d-flex align-items-center justify-content-between py-3">
                          <div className="d-flex align-items-center">
                            <i className="ri-apps-2-line text-primary fs-18 me-2"></i>
                            <h5 className="card-title mb-0 fw-bold">{mod.label}</h5>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-success p-0 text-decoration-none"
                              onClick={() => handleSelectAllModule(mod.module)}
                            >
                              Select All
                            </button>
                            <span className="text-muted">|</span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0 text-decoration-none"
                              onClick={() => handleClearAllModule(mod.module)}
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        <div className="card-body p-3">
                          <div className="row g-2">
                            {mod.actions.map((act) => {
                              const checked = isChecked(mod.module, act.key);
                              const isViewAction = act.key === 'view';

                              return (
                                <div className="col-md-6 col-12" key={act.key}>
                                  <div
                                    className={`form-check py-2 px-3 rounded border d-flex align-items-center mb-0 ${checked ? (isViewAction ? 'bg-primary-subtle border-primary' : 'bg-light border-secondary-subtle') : 'border-light-subtle'}`}
                                    style={{ minHeight: '42px' }}
                                  >
                                    <input
                                      className="form-check-input ms-0 me-2 mt-0"
                                      type="checkbox"
                                      id={`perm-${mod.module}-${act.key}`}
                                      checked={checked}
                                      onChange={() => handleCheckboxChange(mod.module, act.key)}
                                      style={{ cursor: 'pointer', transform: 'scale(1.05)' }}
                                    />
                                    <label
                                      className={`form-check-label mb-0 fs-13 ${checked ? 'fw-bold text-dark' : 'text-muted'}`}
                                      htmlFor={`perm-${mod.module}-${act.key}`}
                                      style={{ cursor: 'pointer', userSelect: 'none' }}
                                    >
                                      {act.label}
                                      {isViewAction && <span className="badge bg-primary text-white ms-2 fs-10">Required</span>}
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Bottom Sticky Action Bar */}
            <div className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg py-3 px-4 z-3" style={{ zIndex: 1000 }}>
              <div className="container-fluid d-flex align-items-center justify-content-between">
                <div>
                  <span className="fw-semibold text-dark me-2">Selected: {selectedPermissions.size} permission(s)</span>
                  {isDirty && <span className="text-warning fw-bold">(Unsaved Edits)</span>}
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-light" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success px-4"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving Permissions...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-3-line me-1"></i> Save Role Permissions
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

          </>
        )}

      </div>
    </div>
  );
};

export default PermissionMatrix;
